import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  extractLotProperties,
  implementsMoleculeLotInterface,
} from 'notebook/integrations/chemistry/custom-evals/LotPropertiesCustomEval';
import {makeMoleculeVersionTypedValue} from 'notebook/integrations/chemistry/custom-evals/MoleculeVersionCustomEval';
import type {StoichiometryStructuredTableRowMetadata} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {
  // eslint-disable-next-line camelcase
  GetLotPropertiesQuery_bioentities_7fiWt0WJ4GzD91Z2J0SVRbzSlLO68dPgO2ZwCE4fEA as LotBioentityNode,
  // eslint-disable-next-line camelcase
  GetLotPropertiesQuery_bioentities_MoleculeNode as MoleculeIdentityNode,
  GetLotPropertiesQuery_Query as LotPropertiesQueryData,
  GetLotPropertiesQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetLotPropertiesQuery';
import {GET_LOT_PROPERTIES_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {ColumnGroup} from '../columns/ColumnDefinitions';
import {LiveCellValue} from '../LiveCellValues';
import {
  StructuredTableRowUpdater,
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableColumnGroups,
} from '../StructuredTableConstants';

/** Union of the two node kinds this updater can fetch/read/update for. */
export type LotOrMoleculeIdentityNode = LotBioentityNode | MoleculeIdentityNode;

/** True if the given bioentities(ids) result is the MoleculeNode identity/version branch, not a Lot. */
export function isMoleculeIdentityNode(node: LotOrMoleculeIdentityNode): node is MoleculeIdentityNode {
  return node.__typename === 'MoleculeNode';
}

/**
 * Build a FLOAT-typed live value that matches the shape the stoichiometry evaluator produces for
 * numeric columns (purity, concentration). Using FLOAT (rather than STRING) is required so that
 * areTypedValuesEquivalent compares like-for-like against the evaluated cell value and the
 * server-stored lastSyncedUpstreamValue: a STRING "12" coerces to "12" while a FLOAT 12 coerces to
 * 12, and lodash.isEqual treats those as unequal, producing a permanent false-positive "outdated"
 * indicator on these columns.
 *
 * Agent keep in sync: _float_live_value in
 * benchling/notebook/structured_tables/resync/row_updaters/stoichiometry.py
 */
function makeFloatLiveValue(value: string): LiveCellValue {
  const preciseDecimal = PreciseDecimal.ofOrUndefined(value);
  if (preciseDecimal == null) {
    // Value came from the lot's own schema tag, so it should always be parsable; but the value
    // may fall back to a raw un-parsable string when extractLotProperties can't canonicalize it
    // (see extractPreciseDecimalValue). Rather than throw and break the row updater, treat it as
    // "no live value" so a malformed lot tag can't produce a false stale/outdated indicator.
    return 'NO_VALUE';
  }
  return {
    type: FieldType.FLOAT,
    value,
    rawValue: value,
    floatValue: preciseDecimal.toFloat(),
  };
}

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<LotOrMoleculeIdentityNode | InaccessibleInfoGraphQL>> {
  const data = await doQuerySafe<LotPropertiesQueryData, GetLotPropertiesQueryVariables>(
    {query: GET_LOT_PROPERTIES_QUERY},
    {
      variables: {
        lotIds: sampleLookupInfos.map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  // Cast is safe: InaccessibleInfoNode satisfies InaccessibleInfo structurally
  return data.bioentities as Array<LotOrMoleculeIdentityNode | InaccessibleInfoGraphQL>;
}

// Agent keep in sync: StoichiometryLotRowUpdater in
// benchling/notebook/structured_tables/resync/row_updaters/stoichiometry.py
export const StoichiometryLotRowUpdater: StructuredTableRowUpdater<
  LotOrMoleculeIdentityNode,
  StoichiometryStructuredTableRowMetadata
> = {
  /**
   * This updater has skipLSUVUpdate: true, so we only return the rowMetadata.
   */
  updateRow(model: LotOrMoleculeIdentityNode | InaccessibleInfoGraphQL, _columns, row, _columnGroup) {
    if (isInaccessible(model) || !isMoleculeIdentityNode(model)) {
      return row;
    }
    return {
      ...row,
      metadata: {
        ...row.metadata,
        moleculeId: model.id,
      },
    };
  },

  /**
   * Purity/concentration/molecule cell values are computed by the stoichiometry custom evals;
   * their LSUV baseline is only set when the reaction group is submitted (via the reaction group
   * submit action function), not by table refresh. skipLSUVUpdate: true tells refreshRowsWithRowUpdater
   * to discard any cell changes from this updater's updateRow (and skip the LSUV recompute
   * entirely when this is the only matched updater), so refreshing the table can't clobber that
   * baseline.
   */
  skipLSUVUpdate: true,

  fetchModel(
    sampleLookupInfo: SampleLookupInfo
  ): Promise<LotOrMoleculeIdentityNode | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<LotOrMoleculeIdentityNode | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  readFromCache({idForLookup}: SampleLookupInfo): LotOrMoleculeIdentityNode | null {
    const data = readQuerySafe<LotPropertiesQueryData, GetLotPropertiesQueryVariables>(
      {query: GET_LOT_PROPERTIES_QUERY},
      {variables: {lotIds: [idForLookup]}}
    );
    const node = data?.bioentities?.[0];
    if (node == null || isInaccessible(node)) {
      return null;
    }
    if (node.__typename === 'MoleculeNode') {
      return node;
    }
    if (!implementsMoleculeLotInterface(node)) {
      return null;
    }
    return node;
  },

  updateCell(
    model: LotOrMoleculeIdentityNode | InaccessibleInfoGraphQL,
    _columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue {
    if (isInaccessible(model)) {
      return 'NO_VALUE';
    }

    if (isMoleculeIdentityNode(model)) {
      // Only currentVersion changes on a Molecule. Molecule Version's live value is the molecule's
      // own currentVersion.id -- unlike the row-metadata approach, this is a normal column with a
      // normal live value, so it goes through the ordinary cellHasOutdatedValue comparison (same
      // mechanism as Purity/Concentration below) with no bespoke staleness plumbing. MW/FW are
      // similar: their evaluated value is pinned to whichever version was resolved when last
      // evaluated (see MolecularWeightCustomEval/FormulaWeightCustomEval), so the live value here is
      // always the *current* version's chemical structure, same as before pinning existed. Every
      // other column has no live value to diff against molecule identity itself.
      switch (column.columnGroup) {
        case StructuredTableColumnGroups.CHEMISTRY_MOLECULE_VERSION:
          return makeMoleculeVersionTypedValue({
            bioentitySummary: {id: model.id},
            versionApiId: model.currentVersion.id,
            modifiedAt: model.currentVersion.modifiedAt,
          });
        case StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT: {
          const molecularWeight = model.currentVersion.chemicalStructure.molecularWeight;
          return molecularWeight.__typename === 'MolecularWeightNode'
            ? makeFloatLiveValue(String(molecularWeight.value))
            : 'NO_VALUE';
        }
        case StructuredTableColumnGroups.CHEMISTRY_FORMULA_WEIGHT: {
          const formulaWeight = model.currentVersion.chemicalStructure.formulaWeight;
          return formulaWeight.__typename === 'FloatPropertyNode'
            ? makeFloatLiveValue(String(formulaWeight.value))
            : 'NO_VALUE';
        }
        default:
          return 'NO_VALUE';
      }
    }

    if (!implementsMoleculeLotInterface(model)) {
      return 'NO_VALUE';
    }

    const properties = extractLotProperties(model.tagsJson);

    switch (column.columnGroup) {
      case StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY:
        return properties.purity != null ? makeFloatLiveValue(properties.purity) : 'NO_VALUE';

      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION:
        if (properties.concentrationMolar != null) {
          return makeFloatLiveValue(properties.concentrationMolar);
        }
        if (properties.concentrationDensity != null) {
          return makeFloatLiveValue(properties.concentrationDensity);
        }
        return 'NO_VALUE';

      // TODO(BNCH-162721): Concentration unit staleness detection deferred until follow-up PR
      // resolves the STRING vs UNIT_REFERENCE type mismatch.
      case StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT:
        return 'NO_VALUE';

      case StructuredTableColumnGroups.CHEMISTRY_MOLECULE:
        return properties.parentMoleculeId != null
          ? {
              type: BaseTypedValueType.BIOENTITY,
              bioentitySummary: {
                id: properties.parentMoleculeId,
                name: properties.parentMoleculeName ?? '',
              },
            }
          : 'NO_VALUE';

      default:
        return 'NO_VALUE';
    }
  },
};
